import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { GraphQLFormattedError } from 'graphql';
import toast from '@lib/components/Toast/Toast';
import _get from 'lodash/get';
import { TFunction } from 'i18next';

export function getGraphQLErrors(
  error: unknown,
): readonly GraphQLFormattedError[] {
  return CombinedGraphQLErrors.is(error) ? error.errors : [];
}

export function getFullMessages(
  graphQLErrors: readonly GraphQLFormattedError[],
  t: TFunction<'translation', undefined>,
): string {
  const messages = (graphQLErrors || [])
    .map(({ extensions }) => {
      if (
        _get(extensions, 'details.task_status.0') ===
        'status_transition_denied_for_task'
      ) {
        return t('error-data-outdated');
      }
      const fullMessages = _get(extensions, 'fullMessages', []) as string[];
      if (fullMessages.length) {
        return fullMessages.join(' ');
      }
      return '';
    })
    .filter(Boolean);
  return [...new Set(messages)].join(' ').trim();
}

/**
 * Extracts per-field validation messages from `extensions.details` when the
 * backend returns it as an array of `{ path, message }` (e.g. VALIDATION_ERROR
 * responses). Returns an empty string when no such details are present.
 */
export function getValidationDetailMessages(
  graphQLErrors: readonly GraphQLFormattedError[],
): string {
  const messages = (graphQLErrors || []).flatMap(({ extensions }) => {
    const details = _get(extensions, 'details');
    if (!Array.isArray(details)) return [];
    return details
      .map((detail) => _get(detail, 'message'))
      .filter(
        (message): message is string =>
          typeof message === 'string' && message.length > 0,
      );
  });
  return [...new Set(messages)].join(' ').trim();
}

export function getErrorMessage(
  error: unknown,
  t: TFunction<'translation', undefined>,
) {
  const fullMessage = getFullMessages(getGraphQLErrors(error), t);
  const message = error instanceof Error ? error.message : undefined;
  return (
    [message, fullMessage].filter(Boolean).join(' ') ||
    t('something-went-wrong')
  );
}

export function showGraphQLErrors(
  error: unknown,
  t: TFunction<'translation', undefined>,
) {
  const fullMessages = getFullMessages(getGraphQLErrors(error), t);
  const message = error instanceof Error ? error.message : undefined;
  toast({
    content: fullMessages || message || t('something-went-wrong'),
  });
}

export function isUpgradePlanRequiredError(error: unknown): boolean {
  if (!error) return false;
  return getGraphQLErrors(error).some(
    ({ extensions }) => _get(extensions, 'code') === 'UPGRADE_PLAN_REQUIRED',
  );
}

/**
 * A locked-plan feature can still throw FORBIDDEN_ERROR instead of
 * UPGRADE_PLAN_REQUIRED when the request races the company's entitlements
 * (e.g. a plan feature gets disabled while the current session's cached
 * `whoami` access flags haven't caught up yet). Callers that gate a screen
 * behind a company-plan access check should treat this the same as an
 * upgrade-required response rather than falling through to a generic error
 * page.
 */
export function isForbiddenError(error: unknown): boolean {
  if (!error) return false;
  return getGraphQLErrors(error).some(
    ({ extensions }) => _get(extensions, 'code') === 'FORBIDDEN_ERROR',
  );
}
